import { Box, Typography } from '@mui/material'
import { DAYS } from 'constants'
import dayjs from 'dayjs'
import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import UnScheduleList from './UnScheduleList'
import TimeRail from './TimeRail'
import DayColumn from './DayColumn'
import PeriodBands from './PeriodBands'
import PeriodColumn from './PeriodColumn'
import NoRecords from 'components/NoRecords'
import { getPeriods } from 'store/lookup'
import { useIsPrinting } from 'utils/print'
import {
  buildBands,
  buildLayout,
  buildPeriodColumn,
  pxPerHourFor,
  HEADER_GAP,
  MIN_DAY_WIDTH,
  PERIOD_WIDTH,
  RAIL_WIDTH,
} from './layout'

const WEEKDAYS = [1, 2, 3, 4, 5]
const BODY_ROW = 3

const PERIOD_COLUMN_ENROLLED_ONLY = true

export default function ScheduleTable({ scheduledSessions, unScheduledSessions }) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const permission = useSelector(state => state.session.permissions)
  const periods = useSelector(state => state.lookup.periods)
  const isPrinting = useIsPrinting()

  const canView = Boolean(parseInt(permission?.['setting.course-session']))
  const sessions = useMemo(() => scheduledSessions ?? [], [scheduledSessions])

  useEffect(() => {
    periods || dispatch(getPeriods())
  }, [dispatch, periods])

  const days = useMemo(() => {
    const present = sessions.map(s => s.dayOfWeek).filter(d => Number.isInteger(d) && d >= 0 && d <= 6)
    return [...new Set([...WEEKDAYS, ...present])].sort((a, b) => a - b)
  }, [sessions])

  const layout = useMemo(() => buildLayout(sessions, days), [sessions, days])
  const bands = useMemo(() => buildBands(periods, layout.range), [periods, layout.range])

  const enrolledPeriodIds = useMemo(
    () => new Set(sessions.map(s => String(s.periodId)).filter(id => id && id !== 'undefined')),
    [sessions],
  )

  const periodBlocks = useMemo(
    () =>
      buildPeriodColumn(periods, layout.range, PERIOD_COLUMN_ENROLLED_ONLY ? enrolledPeriodIds : null),
    [periods, layout.range, enrolledPeriodIds],
  )

  const showPeriods = periodBlocks.length > 0
  const dayStart = showPeriods ? 3 : 2

  const pxPerHour = pxPerHourFor(isPrinting, layout.hourSpan)
  const bodyHeight = layout.hourSpan * pxPerHour
  const today = dayjs().day()

  const unscheduled = useMemo(
    () => [...(unScheduledSessions ?? []), ...layout.invalid.map(s => ({ courseSession: s.list?.[0] }))],
    [unScheduledSessions, layout.invalid],
  )

  if (!sessions.length) {
    return (
      <Box sx={{ p: 2 }}>
        <NoRecords
          message={t('No scheduled classes')}
          description={t('Nothing is scheduled for this marking period.')}
        />
        <UnScheduleList list={unscheduled} permission={canView} />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        p: 2,
        overflowX: 'auto',
        '@media print': { p: 0, overflow: 'visible' },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: [
            `${RAIL_WIDTH}px`,
            showPeriods ? `${PERIOD_WIDTH}px` : null,
            `repeat(${days.length}, minmax(${MIN_DAY_WIDTH}px, 1fr))`,
          ]
            .filter(Boolean)
            .join(' '),
          gridTemplateRows: `auto ${HEADER_GAP}px 1fr`,
          minWidth: RAIL_WIDTH + (showPeriods ? PERIOD_WIDTH : 0) + days.length * MIN_DAY_WIDTH,
          '@media print': { minWidth: 0, breakInside: 'avoid' },
        }}
      >
        <Box sx={{ gridColumn: 1, gridRow: 1, borderBottom: '1px solid', borderColor: 'divider' }} />

        {showPeriods && (
          <Box
            sx={{
              gridColumn: 2,
              gridRow: 1,
              py: 1,
              pr: 2,
              textAlign: 'right',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="subtitle2"
              noWrap
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '.04em',
                fontSize: isPrinting ? 10 : undefined,
                color: 'text.secondary',
              }}
            >
              {t('Periods')}
            </Typography>
          </Box>
        )}

        {days.map((day, index) => (
          <Box
            key={`head-${day}`}
            sx={{
              gridColumn: index + dayStart,
              gridRow: 1,
              position: 'sticky',
              top: 0,
              zIndex: 3,
              py: 1,
              textAlign: 'center',
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              '@media print': { position: 'static' },
            }}
          >
            <Typography
              variant="subtitle2"
              noWrap
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '.04em',
                fontSize: isPrinting ? 10 : undefined,
                fontWeight: day === today ? 600 : 500,
                color: day === today ? 'primary.main' : 'text.secondary',
              }}
            >
              {t(DAYS[day])}
            </Typography>
          </Box>
        ))}

        <TimeRail
          ticks={layout.hourTicks}
          pxPerHour={pxPerHour}
          height={bodyHeight}
          compactText={isPrinting}
          row={BODY_ROW}
        />

        {showPeriods && (
          <PeriodColumn
            periods={periodBlocks}
            pxPerHour={pxPerHour}
            height={bodyHeight}
            compactText={isPrinting}
            column={2}
            row={BODY_ROW}
          />
        )}

        <Box
          sx={{
            gridColumn: `${dayStart} / span ${days.length}`,
            gridRow: BODY_ROW,
            position: 'relative',
            height: bodyHeight,
            zIndex: 0,
          }}
        >
          {layout.hourTicks.map(tick => (
            <Box
              key={tick.minutes}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: tick.offsetHours * pxPerHour,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            />
          ))}
        </Box>

        <PeriodBands
          bands={bands}
          pxPerHour={pxPerHour}
          height={bodyHeight}
          dayCount={days.length}
          column={dayStart}
          row={BODY_ROW}
        />

        {days.map((day, index) => (
          <DayColumn
            key={`col-${day}`}
            entries={layout.byDay.get(day) ?? []}
            pxPerHour={pxPerHour}
            height={bodyHeight}
            column={index + dayStart}
            row={BODY_ROW}
            isToday={day === today}
            isLast={index === days.length - 1}
            canView={canView}
            compactText={isPrinting}
          />
        ))}
      </Box>

      <UnScheduleList list={unscheduled} permission={canView} />
    </Box>
  )
}
